// Display formatters and derived-state helpers for Patient records.
// Wire format → UI text. All masking is FE-only (PRD §4.3); BE returns raw values.

import { normalizePhone } from '@/shared/utils/phone';

// PRD §4.3 (v1.3): patient identifier is the UID, format `U-{id}` (no padding).
// The wire still carries `pid` (`P-{id}`) but the admin UI renders UID everywhere
// (list / panel / CSV) per the identifier rename. Derived from the system id.
export function formatUid(id: number): string {
  return `U-${id}`;
}

// PRD §4.3 name masking:
//   2 chars      → keep first  + '*'                (김철 → 김*)
//   3+ chars     → keep first  + '*'×(len-2) + last (김서연 → 김*연; 김서연우 → 김**우)
// Accepts null so callers can pass nullable wire fields directly (e.g. a withdrawn
// inquirer's name); a null/empty name masks to '' (no crash, matches PRD §4.3 intent).
export function maskName(name: string | null): string {
  if (!name) return '';
  if (name.length <= 1) return name;
  if (name.length === 2) return name[0] + '*';
  return name[0] + '*'.repeat(name.length - 2) + name.slice(-1);
}

// PRD §4.3 phone masking → '010-****-XXXX' (keep first 3 + last 4, mask the middle).
// The canonical stored/wire value is a hyphenless 11-digit string (e.g. '01042433718');
// older rows may still carry the dashed form '010-XXXX-YYYY'. Normalize to digits FIRST
// so both forms mask identically — the previous dashes-only regex returned the canonical
// hyphenless value unchanged, leaking the full number in the list + slide panel.
//
// SHCT-794 — accepts null, like `maskName`. A guardian-mode patient may legitimately have NO
// phone of their own (§3.7: 보호자 모드에서 환자 휴대폰은 선택 입력), and the wire sends null for
// them, which used to reach the cell as an empty string. Masking still yields '' for a missing
// number — the caller decides what an absent value LOOKS like, via `dashIfBlank`; this function
// stays a masker rather than becoming a formatter with its own empty state.
export function maskPhone(phone: string | null | undefined): string {
  const digits = normalizePhone(phone);
  if (digits && digits.length >= 10 && digits.length <= 11) {
    return `${digits.slice(0, 3)}-****-${digits.slice(-4)}`;
  }
  // Not a recognizable phone (empty / wrong length) — return unchanged; never a real number.
  return phone ?? '';
}

// PRD §4.3 email masking: first 3 chars + '***@domain'.
export function maskEmail(email: string | null): string {
  if (!email) return '-';
  const at = email.indexOf('@');
  if (at < 0) return email;
  const local = email.slice(0, at);
  const domain = email.slice(at + 1);
  const head = local.slice(0, 3);
  return `${head}***@${domain}`;
}

// PRD §3.6 spells the panel's empty state as "미입력 시 `-`" for every optional field, so a value the wire
// does not carry has to reach the cell as a dash. `?? '-'` alone covers only null: the wire also sends ""
// (거주지 for a patient who never picked a 구·동 in the app) and whitespace, both of which render as an empty
// cell that looks like a broken panel rather than like "no value". Trim first, then decide. (SHCT-795/797)
export function dashIfBlank(value: string | null | undefined): string {
  const text = value?.trim();
  return text ? text : '-';
}

// PRD §4.3: dates render as YYYY.MM.DD. Wire format is YYYY-MM-DD.
// ISO datetimes (registeredAt) get the date portion in the user's local timezone.
export function formatDate(value: string | null): string {
  if (!value) return '-';
  // Date-only 'YYYY-MM-DD' (no T) → direct string replace; avoids timezone shift.
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value.replace(/-/g, '.');
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}.${pad(d.getMonth() + 1)}.${pad(d.getDate())}`;
}

// KST (UTC+9). The product's times are Korean wall-clock times — PRD 02 §3.6's own example,
// `복약상태 · 2026.08.12 09:14`, is KST. Same constant and same reasoning as
// `features/log-inquiry/utils/format-log.ts`; kept local rather than shared because extracting it
// would edit that live feature for no gain here.
const KST_OFFSET_MS = 9 * 60 * 60 * 1000;

// Does the value pin an instant (`Z` or `±HH:MM`), or is it a bare wall clock?
const HAS_ZONE = /(?:[Zz]|[+-]\d{2}:?\d{2})$/;

// PRD 02 §3.6 마지막 PRO [ADM.063 ①] — the confirmed form is `복약상태 · 2026.08.12 09:14`, so the
// timestamp is printed to the minute.
//
// The wire still sends a date-only value here (admin-api's `PatientDetailResponse.LastPro.submittedAt`
// is a `LocalDate`), and rendering that as `2026.08.12 00:00` would state a submission time nobody
// recorded. So a value carrying no time part keeps the date-only form, and the field starts printing
// the time the moment the server sends one — the BE note covers that half.
//
// The time is rendered in KST, NOT in the viewer's zone. `getHours()` reads the browser's zone, which
// is only KST if the browser happens to sit in Korea — QC runs from ICT (UTC+7), where a `Z` instant
// would print two hours off the value the server recorded and read as a defect. Both wire shapes are
// handled, and they need opposite treatment:
//   • bare wall clock (`2026-08-12T09:14:00`) — the server already wrote KST, so the digits are taken
//     as they stand. Converting would move a time that was never in another zone.
//   • pinned instant (`...Z` / `...+09:00`) — converted to KST.
export function formatDateTime(value: string | null): string {
  if (!value) return '-';
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value.replace(/-/g, '.');
  }
  const pad = (n: number) => String(n).padStart(2, '0');
  const bare = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value);
  if (bare && !HAS_ZONE.test(value)) {
    const [, y, mo, d, h, mi] = bare;
    return `${y}.${mo}.${d} ${h}:${mi}`;
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  // Shift to KST, then read UTC components — the same trick format-log.ts uses.
  const k = new Date(d.getTime() + KST_OFFSET_MS);
  return (
    `${k.getUTCFullYear()}.${pad(k.getUTCMonth() + 1)}.${pad(k.getUTCDate())}` +
    ` ${pad(k.getUTCHours())}:${pad(k.getUTCMinutes())}`
  );
}

// Same formatter, but displays the prototype's "미정 / Pending" placeholder for
// the Discharge Date list column when the value is missing (PRD §4.1).
export function formatDischargeDateList(value: string | null, pendingLabel: string): string {
  return value ? formatDate(value) : pendingLabel;
}

// NOTE: the "ending soon" derivation (active + ≤30 days to expiry → orange chip)
// was deleted in PRD 02 v1.1 (2026.05.04). Expiry is surfaced on the Medical Web
// PRO trend tab instead, so the admin list shows a plain "관리중" chip for all
// active rows. The helpers were removed with PatientStatusBadge's ending branch.
